import { ChangeEvent, useState } from 'react';
import { Box } from '@nimbus-ds/components';
import { ExclamationTriangleIcon } from '@nimbus-ds/icons';
import { EmptyMessage, InteractiveList } from '@nimbus-ds/patterns';
import uniq from 'lodash.uniq';
import { CitiesInterface } from 'domains/Shipping/Addresses/addressesSlice/types';
import GenericSelectionModalHeader from 'domains/Shipping/DeliveryMethods/pages/components/SelectionModals/shared/GenericSelectionModal/GenericSelectionModalHeader';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import CitiesListSkeleton from './Skeleton';
import { getAllCitiesChecked } from '../utils';

interface CitiesListProps {
  cities: CitiesInterface[];
  filteredCities: CitiesInterface[];
  newSelectedCities: string[];
  allChecked: boolean | 'indeterminate';
  hasAllCities: boolean;
  provinceName: string;
  handleSelectAll: (allChecked: boolean | 'indeterminate') => void;
  handleSetFilter: (filter: CitiesInterface[]) => void;
  handleSetNewSelectedCities: (newSelectedCities: string[]) => void;
}

function CitiesList({
  cities,
  filteredCities,
  newSelectedCities,
  allChecked,
  hasAllCities,
  provinceName,
  handleSelectAll,
  handleSetFilter,
  handleSetNewSelectedCities,
}: Readonly<CitiesListProps>) {
  const t = useTranslationShipping();
  const [searchCity, setSearchCity] = useState('');

  const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value.toLowerCase();
    setSearchCity(e.target.value);
    const filteredCities = cities.filter(({ name }) =>
      name.toLowerCase().includes(term),
    );
    handleSetFilter(filteredCities);
  };

  const handleCheckAll = () => {
    if (!allChecked || allChecked === 'indeterminate') {
      const filteredCitiesIds = filteredCities.map((city) => city.id);
      handleSelectAll(getAllCitiesChecked(filteredCitiesIds, cities));
      const selectedCities = uniq([...newSelectedCities, ...filteredCitiesIds]);
      handleSetNewSelectedCities(selectedCities);
    } else {
      handleSelectAll(false);
      handleSetNewSelectedCities([]);
    }
  };

  const handleChange = (city: string, checked: boolean) => {
    let selectedCities = [...newSelectedCities];
    if (checked) {
      selectedCities = [...newSelectedCities, city];
      handleSetNewSelectedCities(selectedCities);
    } else {
      selectedCities = newSelectedCities.filter(
        (selectedCity) => selectedCity !== city,
      );
      handleSetNewSelectedCities(selectedCities);
    }

    handleSelectAll(getAllCitiesChecked(selectedCities, cities));
  };

  const selectedCount = newSelectedCities.length;

  const handleClearSelection = () => {
    handleSetNewSelectedCities([]);
    handleSelectAll(false);
  };

  return (
    <>
      <GenericSelectionModalHeader
        title={provinceName}
        description={t(
          'deliveryMethods.customShipping.selectCitiesModal.description',
        )}
        totalItems={cities.length}
        selectedCount={selectedCount}
        searchTerm={searchCity}
        searchPlaceholder={t(
          'deliveryMethods.customShipping.selectCitiesModal.searchCity',
        )}
        counterText={t(
          'deliveryMethods.customShipping.selectCitiesModal.itemsSelected',
          { count: selectedCount },
        )}
        minItemsForSearch={0}
        onSearchChange={handleChangeSearch}
        onClearSelection={handleClearSelection}
      />

      {filteredCities.length === 0 && !!searchCity && (
        <EmptyMessage
          title={t(
            'deliveryMethods.customShipping.selectCitiesModal.titleSearch',
          )}
          text={t(
            'deliveryMethods.customShipping.selectCitiesModal.messageSearch',
          )}
          icon={<ExclamationTriangleIcon size="large" />}
        />
      )}
      <Box paddingTop="4">
        <InteractiveList>
          {filteredCities.length > 0 && (
            <InteractiveList.CheckboxItem
              title={t(
                'deliveryMethods.customShipping.selectCitiesModal.textSelectAll',
              )}
              checkbox={{
                name: 'selectAll',
                checked:
                  hasAllCities ||
                  (allChecked !== 'indeterminate' ? allChecked : false),
                onChange: handleCheckAll,
                indeterminate: allChecked === 'indeterminate',
              }}
            />
          )}
          {filteredCities.map((city) => (
            <InteractiveList.CheckboxItem
              key={city.id}
              title={city.name}
              checkbox={{
                name: city.id,
                checked: newSelectedCities.includes(city.id),
                onChange: () =>
                  handleChange(city.id, !newSelectedCities.includes(city.id)),
              }}
            />
          ))}
        </InteractiveList>
      </Box>
    </>
  );
}

CitiesList.Skeleton = CitiesListSkeleton;
export default CitiesList;
